import angular from 'angular';
import Chart from 'chart.js/auto';
import '../shared.module';
import appLogoUrl from '../../assets/images/whatsapp.png';

export const APP_BRAND_NAME = 'ZAP CASHPAGO';

export const PDF_PALETTE = {
  primary: '#25D366',
  success: '#16A34A',
  danger: '#DC2626',
  info: '#2563EB',
  warning: '#F59E0B',
  neutral: '#6B7280',
  ink: '#111827',
  muted: '#6B7280',
  line: '#E5E7EB',
  soft: '#F3F4F6'
};

export const PDF_SERIES_COLORS = [
  '#25D366',
  '#2563EB',
  '#F59E0B',
  '#8B5CF6',
  '#DC2626',
  '#0EA5E9',
  '#6B7280'
];

const TONE_COLORS = {
  primary: PDF_PALETTE.primary,
  success: PDF_PALETTE.success,
  danger: PDF_PALETTE.danger,
  info: PDF_PALETTE.info,
  warning: PDF_PALETTE.warning,
  neutral: PDF_PALETTE.neutral
};

const PAGE = { margin: 14, headerHeight: 30, footerHeight: 12 };
const LOGO_BOX = { width: 34, height: 16 };
const LOGO_MAX_PIXELS = 320;
const LOGO_LOAD_TIMEOUT_MS = 1500;
const TEXT_REPLACEMENTS = [
  [/[\u2018\u2019]/g, "'"],
  [/[\u201C\u201D]/g, '"'],
  [/[\u2013\u2014]/g, '-'],
  [/\u2026/g, '...'],
  [/\u2022/g, '-'],
  [/\u00A0/g, ' ']
];

const PRINTABLE_LATIN1 = new RegExp(
  '[^' + String.fromCharCode(9, 10, 13) + '\\u0020-\\u00FF]',
  'g'
);

export function pdfText(value) {
  if (value === null || value === undefined || value === '') return '-';
  const text = TEXT_REPLACEMENTS.reduce(
    (acc, [pattern, replacement]) => acc.replace(pattern, replacement),
    String(value)
  );
  return text.replace(PRINTABLE_LATIN1, '').trim() || '-';
}

export function formatGeneratedAt(date) {
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(date);
}

export function fitInside(width, height, box) {
  if (!width || !height) return { width: 0, height: 0 };
  const scale = Math.min(box.width / width, box.height / height);
  return { width: width * scale, height: height * scale };
}

export function seriesColor(dataset, index) {
  return dataset.cor || PDF_SERIES_COLORS[index % PDF_SERIES_COLORS.length];
}

function hexToRgb(hex) {
  const value = parseInt(hex.slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function chartConfig(chart) {
  const isDoughnut = chart.tipo === 'doughnut';
  const datasets = chart.data.datasets.map((dataset, index) => {
    const color = seriesColor(dataset, index);
    if (isDoughnut) {
      return {
        label: dataset.label,
        data: dataset.data,
        backgroundColor: chart.data.labels.map(
          (_, slice) => (dataset.cores || PDF_SERIES_COLORS)[slice % PDF_SERIES_COLORS.length]
        ),
        borderColor: '#FFFFFF',
        borderWidth: 2
      };
    }
    return {
      label: dataset.label,
      data: dataset.data,
      backgroundColor: chart.tipo === 'line' ? `${color}33` : color,
      borderColor: color,
      borderWidth: chart.tipo === 'line' ? 3 : 0,
      fill: chart.tipo === 'line',
      tension: 0.3,
      pointRadius: 3
    };
  });
  const showLegend = isDoughnut || datasets.length > 1;
  return {
    type: chart.tipo,
    data: { labels: chart.data.labels, datasets },
    options: {
      responsive: false,
      animation: false,
      devicePixelRatio: 1,
      indexAxis: chart.horizontal ? 'y' : 'x',
      cutout: isDoughnut ? '58%' : undefined,
      plugins: {
        legend: {
          display: showLegend,
          position: 'bottom',
          labels: { color: PDF_PALETTE.ink, font: { size: 22 }, boxWidth: 26, padding: 22 }
        }
      },
      scales: isDoughnut
        ? {}
        : {
            x: {
              stacked: Boolean(chart.empilhado),
              grid: { display: Boolean(chart.horizontal), color: PDF_PALETTE.line },
              ticks: { color: PDF_PALETTE.muted, font: { size: 20 }, precision: 0 }
            },
            y: {
              stacked: Boolean(chart.empilhado),
              beginAtZero: true,
              grid: { display: !chart.horizontal, color: PDF_PALETTE.line },
              ticks: { color: PDF_PALETTE.muted, font: { size: 20 }, precision: 0 }
            }
          }
    }
  };
}

class PdfReportService {
  constructor(CompanyBrandingService, UploadService, DownloadService, $document, $window, $q) {
    this.CompanyBrandingService = CompanyBrandingService;
    this.UploadService = UploadService;
    this.DownloadService = DownloadService;
    this.$document = $document;
    this.$window = $window;
    this.$q = $q;
  }

  generate(report) {
    return this.$q.when(this.build(report));
  }

  async build(report) {
    const [{ jsPDF }, autoTableModule, branding] = await Promise.all([
      import(/* webpackChunkName: "pdf" */ 'jspdf'),
      import(/* webpackChunkName: "pdf" */ 'jspdf-autotable'),
      this.loadBranding()
    ]);
    const autoTable = autoTableModule.default;
    const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait', compress: true });
    const generatedAt = formatGeneratedAt(new Date());
    doc.setProperties({ title: pdfText(report.titulo), creator: 'CashPago WhatsApp' });

    let y = this.drawHeader(doc, report, branding, generatedAt);
    y = this.drawKpis(doc, report.kpis || [], y);
    if (report.vazio) {
      y = this.drawNotice(
        doc,
        report.mensagemVazia || 'Nenhum registro encontrado para os filtros selecionados.',
        y
      );
    } else {
      if (report.observacao) y = this.drawNotice(doc, report.observacao, y);
      for (const chart of report.graficos || []) {
        y = this.drawChart(doc, chart, y);
      }
    }
    for (const table of report.tabelas || []) {
      y = this.drawTable(doc, autoTable, table, y);
    }
    this.drawPageChrome(doc, report, branding, generatedAt);
    this.DownloadService.saveBlob(doc.output('blob'), report.arquivo);
    return { pages: doc.internal.getNumberOfPages() };
  }

  async loadBranding() {
    const branding = await this.CompanyBrandingService.get().catch(() => null);
    const companyLogo =
      branding && branding.logoFileId
        ? await this.loadLogo(branding.logoFileId).catch(() => null)
        : null;
    const logo = companyLogo || (await this.rasterize(appLogoUrl).catch(() => null));
    return { nome: (branding && branding.nome) || APP_BRAND_NAME, logo };
  }

  async loadLogo(fileId) {
    const response = await this.DownloadService.fetchBlob(this.UploadService.contentUrl(fileId));
    const blob = response.data;
    if (!blob || !/^image\//.test(blob.type || '')) return null;
    const url = this.$window.URL.createObjectURL(blob);
    try {
      return await this.rasterize(url);
    } finally {
      this.$window.URL.revokeObjectURL(url);
    }
  }

  async rasterize(url) {
    const image = await new Promise((resolve, reject) => {
      const element = new this.$window.Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('Logo inválida'));
      this.$window.setTimeout(
        () => reject(new Error('Logo não carregou a tempo')),
        LOGO_LOAD_TIMEOUT_MS
      );
      element.src = url;
    });
    const size = fitInside(image.naturalWidth, image.naturalHeight, {
      width: LOGO_MAX_PIXELS,
      height: LOGO_MAX_PIXELS
    });
    const canvas = this.$document[0].createElement('canvas');
    canvas.width = Math.max(1, Math.round(Math.min(image.naturalWidth, size.width)));
    canvas.height = Math.max(1, Math.round(Math.min(image.naturalHeight, size.height)));
    canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
    return { dataUrl: canvas.toDataURL('image/png'), width: canvas.width, height: canvas.height };
  }

  drawBrand(doc, branding, x, y, box) {
    if (branding.logo) {
      const size = fitInside(branding.logo.width, branding.logo.height, box);
      doc.addImage(
        branding.logo.dataUrl,
        'PNG',
        x,
        y + (box.height - size.height) / 2,
        size.width,
        size.height,
        'company-logo',
        'FAST'
      );
      return size.width;
    }
    doc.setFillColor(...hexToRgb(PDF_PALETTE.primary));
    doc.roundedRect(x, y, box.height, box.height, 3, 3, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(box.height * 0.9);
    doc.text('Z', x + box.height / 2, y + box.height * 0.72, { align: 'center' });
    return box.height;
  }

  drawHeader(doc, report, branding, generatedAt) {
    const width = doc.internal.pageSize.getWidth();
    const top = PAGE.margin;
    const logoWidth = this.drawBrand(doc, branding, PAGE.margin, top, LOGO_BOX);
    const textX = PAGE.margin + logoWidth + 6;
    doc.setTextColor(...hexToRgb(PDF_PALETTE.muted));
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(pdfText(branding.nome || APP_BRAND_NAME), textX, top + 4);
    doc.setTextColor(...hexToRgb(PDF_PALETTE.ink));
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(17);
    doc.text(pdfText(report.titulo), textX, top + 11);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...hexToRgb(PDF_PALETTE.muted));
    doc.text(pdfText(`${report.modulo} · Período: ${report.periodo}`), textX, top + 16.5);
    doc.text(pdfText(`Gerado em ${generatedAt}`), width - PAGE.margin, top + 4, { align: 'right' });
    if (report.solicitante) {
      doc.text(pdfText(`Solicitado por ${report.solicitante}`), width - PAGE.margin, top + 9, {
        align: 'right'
      });
    }
    doc.setDrawColor(...hexToRgb(PDF_PALETTE.primary));
    doc.setLineWidth(0.8);
    doc.line(PAGE.margin, top + 21, width - PAGE.margin, top + 21);
    if (report.filtros && report.filtros.length) {
      doc.setFontSize(8.5);
      doc.text(pdfText(`Filtros: ${report.filtros.join(' · ')}`), PAGE.margin, top + 26, {
        maxWidth: width - PAGE.margin * 2
      });
      return top + 32;
    }
    return top + 27;
  }

  drawKpis(doc, kpis, y) {
    if (!kpis.length) return y;
    const width = doc.internal.pageSize.getWidth() - PAGE.margin * 2;
    const perRow = Math.min(4, kpis.length);
    const gap = 4;
    const cardWidth = (width - gap * (perRow - 1)) / perRow;
    const cardHeight = 20;
    kpis.forEach((kpi, index) => {
      const row = Math.floor(index / perRow);
      const column = index % perRow;
      const x = PAGE.margin + column * (cardWidth + gap);
      const top = y + row * (cardHeight + gap);
      const color = hexToRgb(TONE_COLORS[kpi.tone] || PDF_PALETTE.primary);
      doc.setFillColor(...hexToRgb(PDF_PALETTE.soft));
      doc.roundedRect(x, top, cardWidth, cardHeight, 2, 2, 'F');
      doc.setFillColor(...color);
      doc.rect(x, top, 1.6, cardHeight, 'F');
      doc.setTextColor(...hexToRgb(PDF_PALETTE.muted));
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(pdfText(kpi.label), x + 5, top + 6.5, { maxWidth: cardWidth - 8 });
      doc.setTextColor(...color);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text(pdfText(kpi.value), x + 5, top + 15);
      if (kpi.detalhe) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.setTextColor(...hexToRgb(PDF_PALETTE.muted));
        doc.text(pdfText(kpi.detalhe), x + cardWidth - 3, top + 15, { align: 'right' });
      }
    });
    return y + Math.ceil(kpis.length / perRow) * (cardHeight + gap) + 2;
  }

  ensureSpace(doc, y, needed) {
    const limit = doc.internal.pageSize.getHeight() - PAGE.margin - PAGE.footerHeight;
    if (y + needed <= limit) return y;
    doc.addPage();
    return PAGE.margin + PAGE.headerHeight - 10;
  }

  drawNotice(doc, text, y) {
    const width = doc.internal.pageSize.getWidth() - PAGE.margin * 2;
    const lines = doc.splitTextToSize(pdfText(text), width - 10);
    const height = lines.length * 4.5 + 7;
    const top = this.ensureSpace(doc, y, height);
    doc.setFillColor(...hexToRgb(PDF_PALETTE.soft));
    doc.roundedRect(PAGE.margin, top, width, height, 2, 2, 'F');
    doc.setTextColor(...hexToRgb(PDF_PALETTE.ink));
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(lines, PAGE.margin + 5, top + 6);
    return top + height + 5;
  }

  renderChart(chart) {
    const document = this.$document[0];
    const host = document.createElement('div');
    host.style.cssText = 'position:fixed;left:-10000px;top:0;width:0;height:0;overflow:hidden;';
    const canvas = document.createElement('canvas');
    canvas.width = chart.tipo === 'doughnut' ? 1100 : 1600;
    canvas.height = chart.tipo === 'doughnut' ? 700 : 640;
    host.appendChild(canvas);
    document.body.appendChild(host);
    try {
      const instance = new Chart(canvas.getContext('2d'), chartConfig(chart));
      const context = canvas.getContext('2d');
      context.globalCompositeOperation = 'destination-over';
      context.fillStyle = '#FFFFFF';
      context.fillRect(0, 0, canvas.width, canvas.height);
      const image = canvas.toDataURL('image/png');
      instance.destroy();
      return { image, ratio: canvas.height / canvas.width };
    } finally {
      document.body.removeChild(host);
    }
  }

  drawChart(doc, chart, y) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const maxWidth = pageWidth - PAGE.margin * 2;
    const { image, ratio } = this.renderChart(chart);
    const width = chart.tipo === 'doughnut' ? maxWidth * 0.62 : maxWidth;
    const height = width * ratio;
    const top = this.ensureSpace(doc, y, height + 10);
    doc.setTextColor(...hexToRgb(PDF_PALETTE.ink));
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(pdfText(chart.titulo), PAGE.margin, top + 4);
    doc.addImage(
      image,
      'PNG',
      PAGE.margin + (maxWidth - width) / 2,
      top + 7,
      width,
      height,
      undefined,
      'FAST'
    );
    return top + height + 12;
  }

  drawTable(doc, autoTable, table, y) {
    const top = this.ensureSpace(doc, y, 24);
    doc.setTextColor(...hexToRgb(PDF_PALETTE.ink));
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(pdfText(table.titulo), PAGE.margin, top + 4);
    const rows = table.linhas.map((row) => table.colunas.map((column) => pdfText(row[column.key])));
    autoTable(doc, {
      startY: top + 7,
      head: [table.colunas.map((column) => pdfText(column.label))],
      body: rows.length
        ? rows
        : [[{ content: 'Nenhum registro encontrado.', colSpan: table.colunas.length }]],
      margin: {
        left: PAGE.margin,
        right: PAGE.margin,
        top: PAGE.margin + PAGE.headerHeight - 10,
        bottom: PAGE.margin + PAGE.footerHeight
      },
      showHead: 'everyPage',
      rowPageBreak: 'avoid',
      styles: {
        font: 'helvetica',
        fontSize: 8,
        cellPadding: 2,
        overflow: 'linebreak',
        textColor: hexToRgb(PDF_PALETTE.ink)
      },
      headStyles: {
        fillColor: hexToRgb(PDF_PALETTE.primary),
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      alternateRowStyles: { fillColor: hexToRgb('#F9FAFB') },
      columnStyles: Object.fromEntries(
        table.colunas.map((column, index) => [
          index,
          { halign: column.align || 'left', cellWidth: column.width || 'auto' }
        ])
      ),
      didParseCell: (data) => {
        const column = table.colunas[data.column.index];
        if (data.section !== 'body' || !column || !column.cores) return;
        const color =
          column.cores[
            table.linhas[data.row.index] &&
              table.linhas[data.row.index][column.toneKey || column.key]
          ];
        if (color) {
          data.cell.styles.textColor = hexToRgb(color);
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });
    return doc.lastAutoTable.finalY + 8;
  }

  drawPageChrome(doc, report, branding, generatedAt) {
    const total = doc.internal.getNumberOfPages();
    const width = doc.internal.pageSize.getWidth();
    const height = doc.internal.pageSize.getHeight();
    for (let page = 1; page <= total; page += 1) {
      doc.setPage(page);
      if (page > 1) {
        const logoWidth = this.drawBrand(doc, branding, PAGE.margin, PAGE.margin - 4, {
          width: 20,
          height: 8
        });
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(...hexToRgb(PDF_PALETTE.ink));
        doc.text(pdfText(report.titulo), PAGE.margin + logoWidth + 4, PAGE.margin + 1.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...hexToRgb(PDF_PALETTE.muted));
        doc.text(pdfText(`Período: ${report.periodo}`), width - PAGE.margin, PAGE.margin + 1.5, {
          align: 'right'
        });
        doc.setDrawColor(...hexToRgb(PDF_PALETTE.line));
        doc.setLineWidth(0.3);
        doc.line(PAGE.margin, PAGE.margin + 5, width - PAGE.margin, PAGE.margin + 5);
      }
      doc.setDrawColor(...hexToRgb(PDF_PALETTE.line));
      doc.setLineWidth(0.3);
      doc.line(
        PAGE.margin,
        height - PAGE.margin - 4,
        width - PAGE.margin,
        height - PAGE.margin - 4
      );
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(...hexToRgb(PDF_PALETTE.muted));
      doc.text(
        pdfText(
          `ZAP CashPago · ${branding.nome || 'Relatório'} · Gerado em ${generatedAt} · Período: ${report.periodo}`
        ),
        PAGE.margin,
        height - PAGE.margin
      );
      doc.text(`Página ${page} de ${total}`, width - PAGE.margin, height - PAGE.margin, {
        align: 'right'
      });
    }
  }
}

PdfReportService.$inject = [
  'CompanyBrandingService',
  'UploadService',
  'DownloadService',
  '$document',
  '$window',
  '$q'
];

angular.module('app.shared').service('PdfReportService', PdfReportService);

export default PdfReportService;
